import React, { useEffect, useState, useCallback } from "react";
import { api, adminApi } from "@/lib/api";
import { LogoLockup } from "@/components/Logo";
import { LogIn, Loader2, LogOut, Trash2, RefreshCw, Mail, Phone, FileSignature, Download, X, Save, MessageSquare, Building2, Eye } from "lucide-react";
import { toast } from "sonner";
import usePageTitle from "@/hooks/usePageTitle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
    usePageTitle("Admin");
    const [authed, setAuthed] = useState(!!sessionStorage.getItem("lended_admin_token"));
    const [pwd, setPwd] = useState("");
    const [loading, setLoading] = useState(false);
    const [leads, setLeads] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [agreements, setAgreements] = useState([]);
    const [applications, setApplications] = useState([]);
    const [openApp, setOpenApp] = useState(null); // selected application for detail drawer
    const [stats, setStats] = useState(null);
    const [tab, setTab] = useState("leads");
    const [filter, setFilter] = useState("all");
    const [appStatusFilter, setAppStatusFilter] = useState("all");

    const load = useCallback(async () => {
        try {
            const [l, c, a, apps, s] = await Promise.all([
                adminApi().get("/leads"),
                adminApi().get("/contact"),
                adminApi().get("/iso-agreements"),
                adminApi().get("/applications"),
                adminApi().get("/admin/stats"),
            ]);
            setLeads(l.data);
            setContacts(c.data);
            setAgreements(a.data);
            setApplications(apps.data);
            setStats(s.data);
        } catch {
            sessionStorage.removeItem("lended_admin_token");
            setAuthed(false);
            toast.error("Session expired. Please log in again.");
        }
    }, []);

    useEffect(() => { if (authed) load(); }, [authed, load]);

    const login = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/admin/login", { password: pwd });
            sessionStorage.setItem("lended_admin_token", data.token);
            setAuthed(true);
            toast.success("Logged in");
        } catch {
            toast.error("Invalid password");
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        sessionStorage.removeItem("lended_admin_token");
        setAuthed(false);
    };

    const removeLead = async (id) => {
        if (!confirm("Delete this lead?")) return;
        try {
            await adminApi().delete(`/leads/${id}`);
            toast.success("Lead deleted");
            load();
        } catch { toast.error("Could not delete"); }
    };

    const exportApplicationsCsv = async () => {
        try {
            const res = await adminApi().get("/applications-export.csv", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "lended-applications.csv";
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Export downloaded");
        } catch { toast.error("Export failed"); }
    };

    if (!authed) {
        return (
            <div className="min-h-[calc(100vh-200px)] flex items-center justify-center bg-[#F8F5F2] py-20" data-testid="admin-login-page">
                <form onSubmit={login} className="bg-white border-2 border-[#0A0A0A] p-10 w-full max-w-md shadow-[8px_8px_0_0_#FF6B1A]">
                    <LogoLockup size={42} />
                    <div className="mt-8">
                        <div className="eyebrow">Admin Portal</div>
                        <h1 className="font-display text-3xl font-bold mt-1">Sign In</h1>
                    </div>
                    <input
                        type="password"
                        required
                        autoFocus
                        value={pwd}
                        onChange={(e) => setPwd(e.target.value)}
                        placeholder="Admin password"
                        className="w-full h-12 px-4 mt-6 border-2 border-black/10 focus:border-[#FF6B1A] outline-none"
                        data-testid="admin-password-input"
                    />
                    <button type="submit" disabled={loading} className="btn-orange w-full justify-center mt-4 disabled:opacity-60" data-testid="admin-login-submit">
                        {loading ? <Loader2 className="animate-spin" size={18} /> : <LogIn size={18} />}
                        {loading ? "Signing in..." : "Sign In"}
                    </button>
                    <p className="text-xs text-black/45 mt-5 text-center font-mono">lended.com — Internal Use</p>
                </form>
            </div>
        );
    }

    const filteredLeads = filter === "all" ? leads : leads.filter((l) => l.audience_type === filter);

    return (
        <div className="bg-[#F8F5F2] min-h-[calc(100vh-200px)] py-12" data-testid="admin-dashboard">
            <div className="container-lended">
                <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
                    <div>
                        <span className="eyebrow">Admin Dashboard</span>
                        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Lead Pipeline</h1>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={load} className="btn-outline-black text-sm" data-testid="admin-refresh">
                            <RefreshCw size={16} /> Refresh
                        </button>
                        <button onClick={logout} className="btn-black text-sm" data-testid="admin-logout">
                            <LogOut size={16} /> Logout
                        </button>
                    </div>
                </div>

                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8" data-testid="admin-stats">
                        {[
                            ["Applications", stats.total_applications ?? 0, "text-[#FF6B1A]"],
                            ["New", stats.new_applications ?? 0, "text-black"],
                            ["Reviewing", stats.reviewing_applications ?? 0, "text-black"],
                            ["Funded", stats.funded_applications ?? 0, "text-green-700"],
                            ["Leads", stats.total_leads, "text-black"],
                            ["Signed ISOs", stats.total_agreements ?? 0, "text-black"],
                        ].map(([l, v, c]) => (
                            <div key={l} className="bg-white border-2 border-[#0A0A0A] p-4">
                                <div className="text-[10px] uppercase tracking-[0.14em] text-black/55">{l}</div>
                                <div className={`stat-num text-3xl md:text-4xl mt-1.5 ${c}`}>{v}</div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex gap-2 mb-4 flex-wrap">
                    {[
                        { v: "applications", label: "Applications (CRM)" },
                        { v: "leads", label: "Funding Leads" },
                        { v: "contacts", label: "Contact Messages" },
                        { v: "agreements", label: "Signed ISO Agreements" },
                    ].map((t) => (
                        <button
                            key={t.v}
                            onClick={() => setTab(t.v)}
                            data-testid={`admin-tab-${t.v}`}
                            className={`px-5 py-3 font-display font-bold text-sm border-2 ${tab === t.v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-black/70 border-black/10"}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {tab === "leads" && (
                    <>
                        <div className="flex gap-2 mb-4">
                            {[["all","All"],["sbo","Small Business"],["iso","ISO"]].map(([v,l]) => (
                                <button
                                    key={v}
                                    onClick={() => setFilter(v)}
                                    data-testid={`filter-${v}`}
                                    className={`px-4 py-2 text-xs font-bold uppercase tracking-wide ${filter === v ? "bg-[#FF6B1A] text-white" : "bg-white text-black/70 border border-black/10"}`}
                                >
                                    {l}
                                </button>
                            ))}
                        </div>
                        <div className="bg-white border-2 border-[#0A0A0A] overflow-x-auto" data-testid="leads-table">
                            <table className="w-full text-sm">
                                <thead className="bg-[#0A0A0A] text-white text-left">
                                    <tr>
                                        {["Type","Name","Business","Phone","Email","Revenue","Funding","Date","Actions"].map((h) => (
                                            <th key={h} className="px-4 py-3 font-display text-xs uppercase tracking-[0.14em]">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredLeads.length === 0 && (
                                        <tr><td colSpan={9} className="py-12 text-center text-black/50">No leads yet.</td></tr>
                                    )}
                                    {filteredLeads.map((l) => (
                                        <tr key={l.id} className="border-t border-black/10 hover:bg-[#F8F5F2]">
                                            <td className="px-4 py-3">
                                                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 font-bold ${l.audience_type === 'iso' ? 'bg-[#FF6B1A] text-white' : 'bg-black text-white'}`}>
                                                    {l.audience_type}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 font-semibold">{l.name}</td>
                                            <td className="px-4 py-3 text-black/70">{l.business_name || "—"}</td>
                                            <td className="px-4 py-3"><a href={`tel:${l.phone}`} className="hover:text-[#FF6B1A]">{l.phone}</a></td>
                                            <td className="px-4 py-3"><a href={`mailto:${l.email}`} className="hover:text-[#FF6B1A]">{l.email}</a></td>
                                            <td className="px-4 py-3 text-xs text-black/70">{l.monthly_revenue || "—"}</td>
                                            <td className="px-4 py-3 text-xs text-black/70">{l.funding_amount || "—"}</td>
                                            <td className="px-4 py-3 text-xs text-black/55 font-mono whitespace-nowrap">
                                                {new Date(l.created_at).toLocaleString()}
                                            </td>
                                            <td className="px-4 py-3">
                                                <button onClick={() => removeLead(l.id)} className="text-black/60 hover:text-red-600" data-testid={`delete-lead-${l.id}`}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {tab === "contacts" && (
                    <div className="bg-white border-2 border-[#0A0A0A] divide-y divide-black/10" data-testid="contacts-list">
                        {contacts.length === 0 && (
                            <div className="py-12 text-center text-black/50">No messages yet.</div>
                        )}
                        {contacts.map((c) => (
                            <div key={c.id} className="p-6 hover:bg-[#F8F5F2]">
                                <div className="flex justify-between items-start gap-6 flex-wrap">
                                    <div>
                                        <div className="font-display font-bold text-lg">{c.name}</div>
                                        <div className="flex items-center gap-4 text-sm text-black/65 mt-1 flex-wrap">
                                            <a href={`mailto:${c.email}`} className="flex items-center gap-1 hover:text-[#FF6B1A]"><Mail size={13} />{c.email}</a>
                                            {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1 hover:text-[#FF6B1A]"><Phone size={13} />{c.phone}</a>}
                                        </div>
                                    </div>
                                    <div className="text-xs font-mono text-black/55">{new Date(c.created_at).toLocaleString()}</div>
                                </div>
                                <div className="text-xs uppercase tracking-wider text-[#FF6B1A] mt-3">{c.subject}</div>
                                <p className="mt-2 text-black/80 leading-relaxed">{c.message}</p>
                            </div>
                        ))}
                    </div>
                )}
                {tab === "applications" && (
                    <>
                        <div className="flex gap-2 mb-4 flex-wrap items-center">
                            {[["all","All"],["new","New"],["reviewing","Reviewing"],["approved","Approved"],["declined","Declined"],["funded","Funded"]].map(([v,l]) => (
                                <button
                                    key={v}
                                    onClick={() => setAppStatusFilter(v)}
                                    data-testid={`app-filter-${v}`}
                                    className={`px-4 py-2 text-xs font-bold uppercase tracking-wide ${appStatusFilter === v ? "bg-[#FF6B1A] text-white" : "bg-white text-black/70 border border-black/10"}`}
                                >
                                    {l}
                                </button>
                            ))}
                            <button
                                onClick={exportApplicationsCsv}
                                className="ml-auto px-4 py-2 text-xs font-bold uppercase tracking-wide bg-[#0A0A0A] text-white inline-flex items-center gap-2 hover:bg-[#FF6B1A] transition-colors"
                                data-testid="export-applications-csv"
                            >
                                <Download size={13} /> Export CSV (Full SSN)
                            </button>
                        </div>
                        <div className="bg-white border-2 border-[#0A0A0A] overflow-x-auto" data-testid="applications-table">
                            <table className="w-full text-sm">
                                <thead className="bg-[#0A0A0A] text-white text-left">
                                    <tr>
                                        {["Status","Business","Industry","Owner","Email","Revenue","Requested","Docs","Submitted"].map((h) => (
                                            <th key={h} className="px-4 py-3 font-display text-xs uppercase tracking-[0.14em]">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {applications.length === 0 && (
                                        <tr><td colSpan={9} className="py-16 text-center text-black/50">No applications yet.</td></tr>
                                    )}
                                    {applications
                                        .filter((a) => appStatusFilter === "all" || a.status === appStatusFilter)
                                        .map((a) => (
                                        <tr key={a.id} onClick={() => setOpenApp(a)} className="border-t border-black/10 hover:bg-[#F8F5F2] cursor-pointer" data-testid={`application-row-${a.id}`}>
                                            <td className="px-4 py-3">
                                                <StatusBadge status={a.status} />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="font-semibold">{a.legal_name}</div>
                                                <div className="text-xs text-black/55">{a.entity_type} · EIN {a.ein}</div>
                                            </td>
                                            <td className="px-4 py-3 text-black/70">{a.industry}</td>
                                            <td className="px-4 py-3">{a.owner_1?.full_name}</td>
                                            <td className="px-4 py-3"><a onClick={(e) => e.stopPropagation()} href={`mailto:${a.owner_1?.email}`} className="hover:text-[#FF6B1A]">{a.owner_1?.email}</a></td>
                                            <td className="px-4 py-3 text-xs font-mono text-black/70">{a.avg_monthly_revenue}</td>
                                            <td className="px-4 py-3 text-xs font-mono text-[#FF6B1A] font-bold">{a.requested_amount}</td>
                                            <td className="px-4 py-3 text-xs">{a.documents?.length || 0}</td>
                                            <td className="px-4 py-3 text-xs text-black/55 font-mono whitespace-nowrap">
                                                {new Date(a.created_at).toLocaleDateString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {openApp && (
                            <ApplicationDrawer
                                application={applications.find((x) => x.id === openApp.id) || openApp}
                                onClose={() => setOpenApp(null)}
                                onChange={load}
                            />
                        )}
                    </>
                )}

                {tab === "agreements" && (
                    <div className="bg-white border-2 border-[#0A0A0A] overflow-x-auto" data-testid="agreements-table">
                        <table className="w-full text-sm">
                            <thead className="bg-[#0A0A0A] text-white text-left">
                                <tr>
                                    {["ISO Legal Name","DBA","Signatory","Owner Email","Bank","Acct ····","Routing ····","Signed","PDF","Action"].map((h) => (
                                        <th key={h} className="px-4 py-3 font-display text-xs uppercase tracking-[0.14em]">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {agreements.length === 0 && (
                                    <tr><td colSpan={10} className="py-12 text-center text-black/50">No signed agreements yet.</td></tr>
                                )}
                                {agreements.map((a) => (
                                    <tr key={a.id} className="border-t border-black/10 hover:bg-[#F8F5F2]">
                                        <td className="px-4 py-3 font-semibold">{a.legal_name}</td>
                                        <td className="px-4 py-3 text-black/70">{a.dba || "—"}</td>
                                        <td className="px-4 py-3">
                                            <div>{a.signatory_name}</div>
                                            <div className="text-xs text-black/55">{a.signatory_title}</div>
                                        </td>
                                        <td className="px-4 py-3"><a href={`mailto:${a.owner_email}`} className="hover:text-[#FF6B1A]">{a.owner_email}</a></td>
                                        <td className="px-4 py-3 text-black/70">{a.bank_name}</td>
                                        <td className="px-4 py-3 font-mono text-xs">····{a.bank_account_last4}</td>
                                        <td className="px-4 py-3 font-mono text-xs">····{a.bank_routing_last4}</td>
                                        <td className="px-4 py-3 text-xs text-black/55 font-mono whitespace-nowrap">
                                            {new Date(a.signed_at).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-3">
                                            <a
                                                href={`${BACKEND_URL}/api/iso-agreements/${a.id}/pdf?token=${encodeURIComponent(sessionStorage.getItem('lended_admin_token') || '')}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-1.5 text-[#FF6B1A] font-bold text-xs hover:underline"
                                                data-testid={`download-agreement-${a.id}`}
                                            >
                                                <Download size={14} /> Download
                                            </a>
                                        </td>
                                        <td className="px-4 py-3">
                                            <button
                                                onClick={async () => {
                                                    if (!confirm("Delete this signed agreement and PDF?")) return;
                                                    try {
                                                        await adminApi().delete(`/iso-agreements/${a.id}`);
                                                        toast.success("Agreement deleted");
                                                        load();
                                                    } catch { toast.error("Delete failed"); }
                                                }}
                                                className="text-black/60 hover:text-red-600"
                                                data-testid={`delete-agreement-${a.id}`}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

const STATUS_STYLES = {
    new: "bg-[#FF6B1A] text-white",
    reviewing: "bg-blue-600 text-white",
    approved: "bg-green-700 text-white",
    declined: "bg-black/80 text-white",
    funded: "bg-emerald-600 text-white",
};

function StatusBadge({ status }) {
    return (
        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 font-bold ${STATUS_STYLES[status] || "bg-black/70 text-white"}`}>
            {status || "new"}
        </span>
    );
}

function ApplicationDrawer({ application, onClose, onChange }) {
    const [status, setStatus] = useState(application.status || "new");
    const [assignedTo, setAssignedTo] = useState(application.assigned_to || "");
    const [offerAmount, setOfferAmount] = useState(application.offer_amount || "");
    const [offerTerms, setOfferTerms] = useState(application.offer_terms || "");
    const [newNote, setNewNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [fullSsns, setFullSsns] = useState(null); // {owner_1_ssn, owner_2_ssn} once revealed
    const [revealing, setRevealing] = useState(false);
    const token = sessionStorage.getItem("lended_admin_token");

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const revealSsns = async () => {
        setRevealing(true);
        try {
            const { data } = await adminApi().get(`/applications/${application.id}/ssn`);
            setFullSsns(data);
        } catch { toast.error("Could not reveal SSN"); }
        finally { setRevealing(false); }
    };

    const save = async () => {
        setSaving(true);
        try {
            await adminApi().patch(`/applications/${application.id}`, {
                status, assigned_to: assignedTo, offer_amount: offerAmount, offer_terms: offerTerms,
            });
            toast.success("Saved");
            onChange();
        } catch { toast.error("Save failed"); }
        finally { setSaving(false); }
    };

    const addNote = async () => {
        if (!newNote.trim()) return;
        try {
            await adminApi().post(`/applications/${application.id}/notes`, { text: newNote });
            setNewNote("");
            toast.success("Note added");
            onChange();
        } catch { toast.error("Could not add note"); }
    };

    const del = async () => {
        if (!confirm(`Delete application for ${application.legal_name}? This also removes uploaded documents.`)) return;
        try {
            await adminApi().delete(`/applications/${application.id}`);
            toast.success("Deleted");
            onClose();
            onChange();
        } catch { toast.error("Delete failed"); }
    };

    const o1 = application.owner_1 || {};
    const o2 = application.owner_2;

    return (
        <div className="fixed inset-0 z-[100] flex justify-end bg-black/60" onClick={onClose} data-testid="app-drawer">
            <div className="w-full max-w-3xl h-full bg-white overflow-y-auto border-l-2 border-[#FF6B1A]" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="sticky top-0 bg-[#0A0A0A] text-white px-6 py-5 flex justify-between items-start border-b-2 border-[#FF6B1A] z-10">
                    <div>
                        <div className="eyebrow text-[#FF6B1A]">Application · {application.id.slice(0, 8)}</div>
                        <h2 className="font-display text-2xl font-bold mt-1">{application.legal_name}</h2>
                        <div className="text-sm text-white/60 mt-1">
                            {application.entity_type} · {application.industry} · submitted {new Date(application.created_at).toLocaleString()}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-white/60 hover:text-white p-2" data-testid="close-drawer"><X size={22} /></button>
                </div>

                <div className="p-6 md:p-8 space-y-8">
                    {/* CRM controls */}
                    <div className="bg-[#F8F5F2] border-2 border-[#0A0A0A] p-5">
                        <div className="eyebrow mb-3">CRM</div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-[10px] uppercase tracking-wider font-bold text-black/60 block mb-1">Status</label>
                                <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full h-10 px-3 border-2 border-black/15 bg-white" data-testid="drawer-status">
                                    <option value="new">New</option>
                                    <option value="reviewing">Reviewing</option>
                                    <option value="approved">Approved</option>
                                    <option value="declined">Declined</option>
                                    <option value="funded">Funded</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider font-bold text-black/60 block mb-1">Assigned to</label>
                                <input value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} placeholder="Ralph" className="w-full h-10 px-3 border-2 border-black/15 bg-white" data-testid="drawer-assigned" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider font-bold text-black/60 block mb-1">Offer amount</label>
                                <input value={offerAmount} onChange={(e) => setOfferAmount(e.target.value)} placeholder="$ amount" className="w-full h-10 px-3 border-2 border-black/15 bg-white" data-testid="drawer-offer-amount" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-wider font-bold text-black/60 block mb-1">Offer terms</label>
                                <input value={offerTerms} onChange={(e) => setOfferTerms(e.target.value)} placeholder="e.g. $12K/mo · 18 mo · 1.28 factor" className="w-full h-10 px-3 border-2 border-black/15 bg-white" data-testid="drawer-offer-terms" />
                            </div>
                        </div>
                        <div className="mt-4 flex gap-2">
                            <button onClick={save} disabled={saving} className="btn-orange text-sm disabled:opacity-60" data-testid="drawer-save">
                                {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
                            </button>
                            <button onClick={del} className="btn-outline-black text-sm text-red-700 hover:bg-red-50 border-red-700" data-testid="drawer-delete">
                                <Trash2 size={14} /> Delete
                            </button>
                        </div>
                    </div>

                    {/* Business snapshot */}
                    <DetailBlock title="Business" icon={Building2}>
                        <Row l="Legal name" v={application.legal_name} />
                        <Row l="Entity" v={application.entity_type} />
                        <Row l="EIN" v={application.ein} />
                        <Row l="Phone" v={application.business_phone} />
                        <Row l="Website" v={application.website} />
                        <Row l="Address" v={application.business_address} />
                        <Row l="Start date" v={application.start_date} />
                        <Row l="Rent / Own" v={application.rent_or_own} />
                        <Row l="Industry" v={application.industry} />
                        <Row l="Open balances" v={application.open_balances} />
                        <Row l="Avg. monthly revenue" v={application.avg_monthly_revenue} />
                        <Row l="Requested amount" v={application.requested_amount} orange />
                        <Row l="Use of funds" v={(application.use_of_funds || []).join(", ")} />
                        <Row l="Description" v={application.description} />
                    </DetailBlock>

                    {/* Owner 1 */}
                    <DetailBlock title="Primary Owner">
                        <Row l="Full name" v={o1.full_name} />
                        <Row l="Email" v={o1.email} />
                        <Row l="Mobile" v={o1.mobile_phone} />
                        <Row l="Ownership %" v={o1.ownership_pct} />
                        <SsnRow masked={o1.ssn} full={fullSsns?.owner_1_ssn} onReveal={revealSsns} revealing={revealing} revealed={!!fullSsns} testId="reveal-ssn-o1" />
                        <Row l="DOB" v={o1.dob} />
                        <Row l="Home address" v={o1.home_address} />
                        <Row l="Credit score" v={o1.credit_score} />
                    </DetailBlock>

                    {o2 && (
                        <DetailBlock title="Second Owner">
                            <Row l="Full name" v={o2.full_name} />
                            <Row l="Email" v={o2.email} />
                            <Row l="Mobile" v={o2.mobile_phone} />
                            <Row l="Ownership %" v={o2.ownership_pct} />
                            <SsnRow masked={o2.ssn} full={fullSsns?.owner_2_ssn} onReveal={revealSsns} revealing={revealing} revealed={!!fullSsns} testId="reveal-ssn-o2" />
                            <Row l="DOB" v={o2.dob} />
                            <Row l="Home address" v={o2.home_address} />
                            <Row l="Credit score" v={o2.credit_score} />
                        </DetailBlock>
                    )}

                    {/* Documents */}
                    <DetailBlock title={`Documents (${(application.documents || []).length})`}>
                        {(application.documents || []).length === 0 ? (
                            <p className="text-sm text-black/55">No documents attached.</p>
                        ) : (
                            <ul className="space-y-2 col-span-2">
                                {application.documents.map((d) => (
                                    <li key={d.filename} className="flex items-center gap-3 bg-white border border-black/10 p-3">
                                        <FileSignature className="text-[#FF6B1A] shrink-0" size={18} />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold truncate">{d.original_name}</div>
                                            <div className="text-xs text-black/55">{(d.size / 1024).toFixed(0)} KB · {d.content_type}</div>
                                        </div>
                                        <a
                                            href={`${BACKEND_URL}/api/applications/${application.id}/documents/${d.filename}?token=${encodeURIComponent(token || "")}`}
                                            target="_blank" rel="noreferrer"
                                            className="text-[#FF6B1A] font-bold text-xs hover:underline inline-flex items-center gap-1"
                                            data-testid={`download-doc-${d.filename}`}
                                        >
                                            <Download size={14} /> Download
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </DetailBlock>

                    {/* Signature */}
                    <DetailBlock title="Signature">
                        <Row l="Typed signature" v={application.typed_signature} />
                        <Row l="Signed date" v={application.signed_date} />
                        <Row l="Authorization" v={application.authorized ? "Agreed" : "Not agreed"} />
                    </DetailBlock>

                    {/* Notes */}
                    <div>
                        <div className="eyebrow mb-3 flex items-center gap-2"><MessageSquare size={14} className="text-[#FF6B1A]" /> CRM Notes</div>
                        <div className="space-y-3 mb-4">
                            {(application.notes || []).length === 0 && <p className="text-sm text-black/50">No notes yet.</p>}
                            {(application.notes || []).map((n) => (
                                <div key={n.id} className="bg-[#F8F5F2] p-3 border-l-4 border-[#FF6B1A]">
                                    <div className="text-xs text-black/55 mb-1 font-mono">{n.author} · {new Date(n.created_at).toLocaleString()}</div>
                                    <div className="text-sm">{n.text}</div>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            <input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note…" className="flex-1 h-10 px-3 border-2 border-black/15" data-testid="drawer-note-input" />
                            <button onClick={addNote} className="btn-black text-sm" data-testid="drawer-add-note">Add</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SsnRow({ masked, full, onReveal, revealing, revealed, testId }) {
    const shown = revealed ? (full || "—") : (masked || "—");
    return (
        <div className="flex justify-between items-center gap-4 py-1.5 border-b border-black/5 last:border-b-0" data-testid={`${testId}-row`}>
            <span className="text-xs uppercase tracking-wider text-black/55 shrink-0">SSN</span>
            <span className="flex items-center gap-2">
                <span className={`text-sm text-right ${revealed && full ? "font-mono font-bold text-[#FF6B1A]" : ""}`} data-testid={`${testId}-value`}>
                    {shown}
                </span>
                {!revealed && (
                    <button
                        type="button"
                        onClick={onReveal}
                        disabled={revealing}
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 border border-[#FF6B1A] text-[#FF6B1A] hover:bg-[#FF6B1A] hover:text-white transition-colors inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={testId}
                    >
                        {revealing ? <Loader2 className="animate-spin" size={11} /> : <Eye size={11} />} Reveal
                    </button>
                )}
            </span>
        </div>
    );
}

function DetailBlock({ title, icon: Icon, children }) {
    return (
        <div>
            <div className="eyebrow mb-3 flex items-center gap-2">
                {Icon && <Icon size={14} className="text-[#FF6B1A]" />} {title}
            </div>
            <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 bg-white border border-black/10 p-4">
                {children}
            </div>
        </div>
    );
}

function Row({ l, v, orange }) {
    return (
        <div className="flex justify-between gap-4 py-1.5 border-b border-black/5 last:border-b-0">
            <span className="text-xs uppercase tracking-wider text-black/55 shrink-0">{l}</span>
            <span className={`text-sm text-right ${orange ? "font-mono font-bold text-[#FF6B1A]" : ""}`}>{v || "—"}</span>
        </div>
    );
}
